<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-start justify-center pt-24 bg-black/60"
      @click.self="close"
    >
      <div class="w-full max-w-xl bg-gray-800 rounded-lg shadow-2xl overflow-hidden">

        <!-- Breadcrumb shown in sub-modes -->
        <div v-if="mode !== 'commands'" class="flex items-center gap-2 px-4 pt-3 pb-1 select-none">
          <button
            class="text-xs text-gray-500 hover:text-gray-300 transition-colors"
            @click="backToCommands"
          >Commands</button>
          <span class="text-gray-600 text-xs">›</span>
          <span class="text-xs text-gray-300">{{ modeLabel }}</span>
        </div>

        <!-- Input row: artisan mode prefixes "php artisan" before the text field -->
        <div class="flex items-center border-b border-gray-700">
          <span
            v-if="mode === 'artisan'"
            class="pl-4 pr-1 text-sm text-gray-400 whitespace-nowrap select-none"
          >php artisan</span>
          <input
            ref="searchInput"
            v-model="query"
            class="flex-1 bg-transparent text-gray-100 py-3 text-sm focus:outline-none"
            :class="mode === 'artisan' ? 'pl-1 pr-4' : 'px-4'"
            :placeholder="inputPlaceholder"
            autocomplete="off"
            spellcheck="false"
            @keydown.escape.prevent="handleEscape"
            @keydown.down.prevent="moveDown"
            @keydown.up.prevent="moveUp"
            @keydown.enter.prevent="execute"
          />
        </div>

        <!-- Results -->
        <ul class="max-h-72 overflow-y-auto py-1">

          <!-- Command list -->
          <template v-if="mode === 'commands'">
            <li
              v-for="(cmd, i) in filteredCommands"
              :key="cmd.id"
              class="flex items-center gap-3 px-4 py-2 text-sm cursor-pointer select-none"
              :class="i === selectedIdx
                ? 'bg-blue-600 text-white'
                : 'text-gray-200 hover:bg-gray-700'"
              @click="selectCommand(cmd)"
            >
              <span class="w-5 text-center shrink-0 text-base">{{ cmd.icon }}</span>
              <span class="flex-1">{{ cmd.label }}</span>
              <span v-if="cmd.shortcut" class="text-[11px] opacity-50 shrink-0">{{ cmd.shortcut }}</span>
            </li>
          </template>

          <!-- File list: open files filtered by query + "open path" row -->
          <template v-else-if="mode === 'files'">
            <li
              v-for="(file, i) in filteredFiles"
              :key="file.path"
              class="flex items-center gap-3 px-4 py-2 text-sm cursor-pointer select-none"
              :class="i === selectedIdx
                ? 'bg-blue-600 text-white'
                : 'text-gray-200 hover:bg-gray-700'"
              @click="openFile(file.path)"
            >
              <span class="w-5 text-center shrink-0">📄</span>
              <span class="flex-1 truncate font-mono text-xs">{{ file.path }}</span>
            </li>
            <!-- "Open path" row: shown when the query doesn't match any open file -->
            <li
              v-if="query && !exactFileMatch"
              class="flex items-center gap-3 px-4 py-2 text-sm cursor-pointer select-none"
              :class="selectedIdx === filteredFiles.length
                ? 'bg-blue-600 text-white'
                : 'text-gray-400 hover:bg-gray-700'"
              @click="openFile(query)"
            >
              <span class="w-5 text-center shrink-0">↗</span>
              <span class="flex-1 truncate font-mono text-xs">Open: {{ query }}</span>
            </li>
          </template>

          <!-- Branch list -->
          <template v-else-if="mode === 'branches'">
            <li v-if="loadingBranches" class="px-4 py-2 text-xs text-gray-500 select-none">
              Loading branches…
            </li>
            <li
              v-for="(branch, i) in filteredBranches"
              :key="branch"
              class="flex items-center gap-3 px-4 py-2 text-sm cursor-pointer select-none"
              :class="i === selectedIdx
                ? 'bg-blue-600 text-white'
                : 'text-gray-200 hover:bg-gray-700'"
              @click="switchBranch(branch)"
            >
              <span class="w-5 text-center shrink-0 text-base">⎇</span>
              <span class="flex-1 truncate">{{ branch }}</span>
              <span
                v-if="branch === gitStore.currentBranch"
                class="text-[11px] opacity-50 shrink-0"
              >current</span>
            </li>
          </template>

          <!-- Artisan: just a hint line; user types in the input above -->
          <template v-else-if="mode === 'artisan'">
            <li class="px-4 py-2 text-xs text-gray-500 select-none">
              Type the artisan subcommand and press Enter to run in terminal
            </li>
          </template>

          <!-- Empty state -->
          <li v-if="showEmpty" class="px-4 py-2 text-xs text-gray-500 select-none">
            No results
          </li>

        </ul>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch } from 'vue'
import { useEditorStore }   from '@/stores/editorStore'
import { useTerminalStore } from '@/stores/terminalStore'
import { useRepoStore }     from '@/stores/repoStore'
import { useGitStore }      from '@/stores/gitStore'
import { getSocket }        from '@/websocket/wsClient'

// ── Props / emits ─────────────────────────────────────────────────────────────

const isOpen = defineModel<boolean>({ required: true })

/**
 * Emitted when a command needs to switch the sidebar panel (e.g. Ask Claude).
 * App.vue handles this by calling handleActivate(panel).
 */
const emit = defineEmits<{
  (e: 'switch-panel', panel: string): void
}>()

// ── Stores ────────────────────────────────────────────────────────────────────

const editorStore   = useEditorStore()
const terminalStore = useTerminalStore()
const repoStore     = useRepoStore()
const gitStore      = useGitStore()

// ── Input state ───────────────────────────────────────────────────────────────

const query         = ref('')
const selectedIdx   = ref(0)
const searchInput   = ref<HTMLInputElement>()
const loadingBranches = ref(false)

type Mode = 'commands' | 'files' | 'branches' | 'artisan'
const mode = ref<Mode>('commands')

// ── Command definitions ───────────────────────────────────────────────────────

interface Command {
  id:       string
  icon:     string
  label:    string
  shortcut: string
  action:   () => void | Promise<void>
}

const COMMANDS: Command[] = [
  {
    id:       'open-file',
    icon:     '📄',
    label:    'Open File',
    shortcut: '',
    action:   () => enterMode('files'),
  },
  {
    id:       'switch-branch',
    icon:     '⎇',
    label:    'Switch Branch',
    shortcut: '',
    action:   () => enterBranchMode(),
  },
  {
    id:       'format',
    icon:     '✨',
    label:    'Format File',
    shortcut: 'Alt+Shift+F',
    action:   () => { editorStore.formatCurrentFile(); close() },
  },
  {
    id:       'artisan',
    icon:     '⚙',
    label:    'Run Artisan',
    shortcut: '',
    action:   () => enterMode('artisan'),
  },
  {
    id:       'ask-claude',
    icon:     '✦',
    label:    'Ask Claude',
    shortcut: '',
    action:   () => { emit('switch-panel', 'claude'); close() },
  },
  {
    id:       'terminal',
    icon:     '>_',
    label:    'Open Terminal',
    shortcut: 'Ctrl+`',
    action:   () => {
      if (terminalStore.tabs.length === 0) {
        terminalStore.openTerminal(repoStore.activeRepoPath || '/')
      } else {
        terminalStore.isOpen = true
      }
      close()
    },
  },
]

// ── Filtered lists ────────────────────────────────────────────────────────────

const filteredCommands = computed(() => {
  const q = query.value.toLowerCase()
  return q ? COMMANDS.filter(c => c.label.toLowerCase().includes(q)) : COMMANDS
})

const filteredFiles = computed(() => {
  const q = query.value.toLowerCase()
  return q
    ? editorStore.openFiles.filter(f => f.path.toLowerCase().includes(q))
    : editorStore.openFiles
})

// True when the query exactly matches a currently open file path.
const exactFileMatch = computed(() =>
  editorStore.openFiles.some(f => f.path === query.value)
)

const filteredBranches = computed(() => {
  const q = query.value.toLowerCase()
  return q
    ? gitStore.branches.filter(b => b.toLowerCase().includes(q))
    : gitStore.branches
})

// ── UI computed ───────────────────────────────────────────────────────────────

const modeLabel = computed((): string => {
  switch (mode.value) {
    case 'files':    return 'Open File'
    case 'branches': return 'Switch Branch'
    case 'artisan':  return 'Run Artisan'
    default:         return ''
  }
})

const inputPlaceholder = computed((): string => {
  switch (mode.value) {
    case 'commands': return 'Type a command…'
    case 'files':    return 'Filter open files, or type a server path…'
    case 'branches': return 'Filter branches…'
    case 'artisan':  return 'cache:clear'
  }
})

const showEmpty = computed((): boolean => {
  if (mode.value === 'commands')  return filteredCommands.value.length === 0
  if (mode.value === 'files')     return filteredFiles.value.length === 0 && !query.value
  if (mode.value === 'branches')  return filteredBranches.value.length === 0 && !loadingBranches.value
  return false
})

// Total navigable rows in the current list (used for arrow-key clamping).
function listLength(): number {
  switch (mode.value) {
    case 'commands': return filteredCommands.value.length
    case 'files':    return filteredFiles.value.length + (query.value && !exactFileMatch.value ? 1 : 0)
    case 'branches': return filteredBranches.value.length
    case 'artisan':  return 0
  }
}

// ── Mode transitions ──────────────────────────────────────────────────────────

function enterMode(m: Mode): void {
  mode.value      = m
  query.value     = ''
  selectedIdx.value = 0
  nextTick(() => searchInput.value?.focus())
}

async function enterBranchMode(): Promise<void> {
  enterMode('branches')
  if (gitStore.branches.length === 0 && repoStore.activeRepoPath) {
    loadingBranches.value = true
    await gitStore.fetchBranches(repoStore.activeRepoPath)
    loadingBranches.value = false
  }
}

function backToCommands(): void {
  mode.value      = 'commands'
  query.value     = ''
  selectedIdx.value = 0
  nextTick(() => searchInput.value?.focus())
}

// ── Actions ───────────────────────────────────────────────────────────────────

function openFile(path: string): void {
  if (!path.trim()) return
  editorStore.openFile(path)
  close()
}

function switchBranch(branch: string): void {
  gitStore.checkout(branch)
  close()
}

async function runArtisan(): Promise<void> {
  const args = query.value.trim()
  if (!args) return

  // Ensure the terminal panel is visible with at least one session.
  if (terminalStore.tabs.length === 0) {
    terminalStore.openTerminal(repoStore.activeRepoPath || '/')
  }
  terminalStore.isOpen = true

  // Wait for Vue to render TerminalPanel (nextTick) and for the server PTY to
  // finish spawning (400 ms is generous on a local socket connection).
  await nextTick()
  await new Promise<void>(r => setTimeout(r, 400))

  const sessionId = terminalStore.activeTab
  if (sessionId) {
    getSocket().emit('terminal:input', { sessionId, input: `php artisan ${args}\n` })
  }

  close()
}

// ── Keyboard navigation ───────────────────────────────────────────────────────

function moveDown(): void {
  const max = listLength() - 1
  if (max < 0) return
  selectedIdx.value = Math.min(selectedIdx.value + 1, max)
}

function moveUp(): void {
  selectedIdx.value = Math.max(selectedIdx.value - 1, 0)
}

function execute(): void {
  if (mode.value === 'commands') {
    const cmd = filteredCommands.value[selectedIdx.value]
    if (cmd) selectCommand(cmd)
    return
  }
  if (mode.value === 'files') {
    const file = filteredFiles.value[selectedIdx.value]
    if (file) { openFile(file.path); return }
    // selectedIdx points at the "Open path" row
    if (query.value && !exactFileMatch.value) openFile(query.value)
    return
  }
  if (mode.value === 'branches') {
    const branch = filteredBranches.value[selectedIdx.value]
    if (branch) switchBranch(branch)
    return
  }
  if (mode.value === 'artisan') {
    runArtisan()
  }
}

function selectCommand(cmd: Command): void {
  cmd.action()
}

function handleEscape(): void {
  if (mode.value !== 'commands') {
    backToCommands()
  } else {
    close()
  }
}

// ── Lifecycle ─────────────────────────────────────────────────────────────────

function close(): void {
  isOpen.value = false
}

// Reset to command mode and focus the input whenever the palette opens.
watch(isOpen, async (val) => {
  if (val) {
    mode.value      = 'commands'
    query.value     = ''
    selectedIdx.value = 0
    await nextTick()
    searchInput.value?.focus()
  }
})

// Always reset selection to the top when the query changes so the user
// never has a stale selectedIdx pointing past the end of a shorter list.
watch(query, () => { selectedIdx.value = 0 })
</script>
